import { computed } from "vue";
import { isCodificationCard, isOpenRecodeCard } from "@utils/hiddenCardQuestionType";

export function useVerbatims({ cardType, questionMeta, activeIteration }) {
  // Open AI recode: free-text answers stored on the recode question itself, shown as verbatims.
  const isOpenRecode = computed(() => isOpenRecodeCard(questionMeta.value));

  const isOpenQuestion = computed(() => {
    if (["q_open", "q_open_multiple", "q_zipcode", "q_city", "q_email", "q_image", "q_video", "q_audio"].includes(cardType.value)) return true;
    // Open recode (e.g. URL-parameter "psid"): free values on its own question, shown as verbatims.
    return (
      cardType.value === "q_hidden" &&
      !isOpenRecode.value &&
      questionMeta.value?.questions?.[0]?.question_type === "open"
    );
  });

  const isRecodeQuestion = computed(() => isCodificationCard(questionMeta.value));

  const isMediaQuestion = computed(() => ["q_image", "q_video", "q_audio"].includes(cardType.value));

  // For closed questions with "Other" option: the companion _Other_ question
  // lives inside the same card as the main question — its name ends in "_Other_".
  const otherQuestionId = computed(() => {
    if (!questionMeta.value || isOpenQuestion.value || isRecodeQuestion.value) return null;
    const otherQ = (questionMeta.value.questions || []).find((q) => /_Other_$/.test(q?.name || ""));
    return otherQ?.id || null;
  });

  // Answer code of the "Other" choice, used to expand that bar inline.
  const otherAnswerCode = computed(() => {
    const answers = questionMeta.value?.specs?.answers;
    if (!answers?.length) return null;
    const other = answers.find((a) => a?.is_other);
    return other?.code != null ? String(other.code) : null;
  });

  const hasVerbatims = computed(() => isOpenQuestion.value || isRecodeQuestion.value || !!otherQuestionId.value);

  const ownQuestionId = computed(
    () => activeIteration.value?.question_id || questionMeta.value?.questions?.[0]?.id,
  );

  const verbatimsQuestionId = computed(() => {
    // Open recode stores its verbatims on its own question (no source question).
    if (isOpenRecode.value) return ownQuestionId.value;
    if (isRecodeQuestion.value) return questionMeta.value?.source_question_id;
    if (isOpenQuestion.value) return ownQuestionId.value;
    return otherQuestionId.value;
  });

  // The recode question's own ID (for the category recode_verbatims endpoint).
  // Open recodes have no categories, so there is nothing to drill into per code.
  const recodeQuestionId = computed(() => {
    if (isOpenRecode.value) return null;
    if (isRecodeQuestion.value) return ownQuestionId.value;
    return null;
  });

  return {
    isOpenQuestion,
    isOpenRecode,
    isRecodeQuestion,
    isMediaQuestion,
    otherQuestionId,
    otherAnswerCode,
    hasVerbatims,
    verbatimsQuestionId,
    recodeQuestionId,
  };
}
